import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { isEmpty } from 'lodash';
import React from 'react';

import { IBrandSafetyRuleException } from '~/src/dash/api/brand-safety-rules';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { borderRadius, ButtonV2, Div, Text, TextButton } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { ActionPill } from '~/src/dash/views/brand-safety/Rule/ActionPill';
import { isLanguageOversized } from '~/src/locales/common';
import { usePublicaTranslation } from '~/src/locales/en';

export interface IIABCategoryTableItems {
  included: boolean; // is element included or excluded
  title: string; // visible text on the table
  name: string; // name of the code
  ID: string; // ID
}

// component properties
interface IIABCategoryTableProps {
  list: IIABCategoryTableItems[]; // list of element to display in the table
  handleChange: (newList: IIABCategoryTableItems[], title: string) => void;
  handleOpenRuleTargetedExceptionDialog: (targetedExceptionIABCode: string) => void;
  brandSafetyRuleExceptions?: IBrandSafetyRuleException[]; // all brand safety rule exceptions
  isException?: boolean; // if true, the table is used to display exceptions
}

export const IABCategoryTable = ({
  brandSafetyRuleExceptions,
  list,
  handleChange,
  handleOpenRuleTargetedExceptionDialog,
  isException = false, // if true, the table is used to display exceptions
}: IIABCategoryTableProps): React.ReactNode => {
  const { i18n } = usePublicaTranslation();

  // handles the toggle between included/excluded on an element
  const handleIncludeChange = (index: number, title: string): void => {
    const menuList = [...list];
    menuList[index].included = !menuList[index].included; // toggle included boolean
    handleChange(menuList, title); // callback
  };

  // handles the deletion of an element
  const handleDelete = (index: number, title: string): void => {
    const menuList = [...list];
    menuList.splice(index, 1); // delete element
    handleChange(menuList, title); // callback
  };

  return (
    <TableContainer
      component={Paper}
      style={{
        maxWidth: '720px',
        boxShadow: 'none',
        borderRadius: borderRadius.base,
        border: `1px solid ${publicaColors.lightGray}`,
        borderBottom: 'none',
      }}
    >
      <Table size='small'>
        <TableHead style={{ backgroundColor: publicaColors.veryLightGray }}>
          <TableRow>
            <TableCell align='center'>
              <Text small bold tKey='Status' />
            </TableCell>
            <TableCell>
              <Text small bold tKey='IABCode' />
            </TableCell>
            <TableCell>
              <Text small bold tKey='IABCategory' />
            </TableCell>
            <TableCell align='center'>
              <Text small bold tKey='Exceptions' />
            </TableCell>
            <TableCell align='center'>
              <Text small bold tKey='Delete' />
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {isEmpty(list) ? (
            <TableRow>
              <TableCell align='center'>
                <Text small tKey='None' />
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          ) : (
            list.map((element: IIABCategoryTableItems, index: number) => {
              const foundTargetedIABCategory =
                brandSafetyRuleExceptions &&
                brandSafetyRuleExceptions.find((brandSafetyRuleException) => {
                  return (
                    brandSafetyRuleException.TargetedIABExceptions.split(',').findIndex(
                      (iabException) => iabException === element.title,
                    ) !== -1
                  );
                });

              const isBlocked = !isException && element.included; // if isException is false, then we are in the main rule table and we consider that included means blocked

              return (
                <TableRow>
                  <TableCell align='center'>
                    <ActionPill
                      isBlocked={isBlocked}
                      isButton={false}
                      isDisabled={isException}
                      muted={false}
                      onClick={() => handleIncludeChange(index, element.title)}
                    />
                  </TableCell>

                  <TableCell>
                    <Text small>{element.title}</Text>
                  </TableCell>

                  <TableCell>
                    <Text
                      small={!isLanguageOversized(i18n.language)}
                      extraSmall={isLanguageOversized(i18n.language)}
                    >
                      {element.name}
                    </Text>
                  </TableCell>

                  <TableCell align='center'>
                    <TextButton
                      disabled={!foundTargetedIABCategory}
                      onClick={() => handleOpenRuleTargetedExceptionDialog(element.title)}
                    >
                      <IconV2
                        name='eye'
                        large
                        color={
                          foundTargetedIABCategory ? publicaColors.gray : publicaColors.lightGray
                        }
                      />
                    </TextButton>
                  </TableCell>

                  <TableCell align='center'>
                    <ButtonV2 clearBackground onClick={() => handleDelete(index, element.title)}>
                      <Div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <IconV2 name='delete' large color={publicaColors.gray} />
                      </Div>
                    </ButtonV2>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
